<template>
    <div id="monsters">
        <div id="main">
            <monster-box
                v-if="monsterToView"
                :monster="monsterToView"
                :theme="theme"
                :encounterId="encounterId"
                :monsterIndex="monsterIndex"
                @remove-monster="removeMonster"
                @roll-dice="openDiceBox"
            />
        </div>

        <div id="sidebar">
            <button
                type="button"
                class="btn btn-primary w-100 mb-3"
                data-bs-toggle="modal"
                data-bs-target="#bestiaryModal"
                aria-controls="bestiaryModal"
            >
                <font-awesome-icon icon="skull" /> Add
            </button>

            <!-- Sidebar monster cards -->
            <div v-if="monsters && monsters.length">
                <div
                    class="card bg-dark text-light mb-3"
                    v-for="(monster, index) in monsters"
                    :key="monster.name + index"
                    :class="{ 'active-monster': index === monsterIndex }"
                    @click="
                        monsterToView = monster;
                        monsterIndex = index;
                    "
                >
                    <div
                        class="card-body d-flex flex-column align-items-stretch"
                    >
                        <h5 class="card-title mb-2 text-center">
                            {{ monster.name }}
                        </h5>

                        <!-- Up/Down Buttons: full width beneath name -->
                        <div class="btn-group w-100">
                            <button
                                class="btn btn-sm btn-secondary w-50"
                                @click.stop="moveMonster(index, -1)"
                                :disabled="index === 0"
                            >
                                ↑
                            </button>
                            <button
                                class="btn btn-sm btn-secondary w-50"
                                @click.stop="moveMonster(index, 1)"
                                :disabled="index === monsters.length - 1"
                            >
                                ↓
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div v-else class="text-center text-muted mt-3">
                <em>No monsters added yet.</em>
            </div>
        </div>
    </div>

    <!-- Bestiary Modal -->
    <bestiary-modal :story-id="storyId" :encounter-id="encounterId" />

    <dice-box
        v-if="monsterToView && diceRoller"
        ref="diceBox"
        :theme="theme"
        :clickedSkillKey="clickedSkillKey"
    />
</template>

<script>
import BestiaryModal from "./BestiaryModal.vue";
import MonsterBox from "./MonsterBox.vue";
import { useRosterStore } from "@/stores/index";
import DiceBox from "./DiceBox.vue";

export default {
    name: "Monsters",
    components: { BestiaryModal, MonsterBox, DiceBox },
    props: {
        monsters: {
            type: Array,
            required: true,
        },
        storyId: {
            type: Number,
            required: true,
        },
        encounterId: {
            type: Number,
            required: true,
        },
        theme: {
            type: Number,
            required: true,
        },
    },
    data() {
        return {
            store: useRosterStore(),
            monsterToView: null,
            monsterIndex: null,
            diceRoller: true,
            clickedSkillKey: "grit",
        };
    },
    mounted() {
        if (this.monsters.length) {
            this.monsterToView = this.monsters[0];
            this.monsterIndex = 0;
        }
    },
    methods: {
        openDiceBox(statKey) {
            this.clickedSkillKey = statKey.statKey;
            if (!this.diceRoller || !this.$refs.diceBox) return;
            this.$refs.diceBox.showModal(
                statKey.skill,
                0,
                statKey.rolltype,
                statKey.monsterType
            );
        },

        setMonstersHeight() {
            this.$nextTick(() => {
                const monstersEl = this.$el.querySelector("#monsters");
                if (!monstersEl) return;

                // Sum height of everything above monsters
                let offset = 0;
                let sibling = monstersEl;
                while ((sibling = sibling.previousElementSibling)) {
                    offset += sibling.offsetHeight;
                }

                // Set height to remaining viewport height
                monstersEl.style.height = `${window.innerHeight - offset}px`;
            });
        },

        /** ❌ Remove a monster and update store */
        removeMonster(monsterName) {
            const story = this.store.getStoryById(this.storyId);
            if (!story) return;

            const encounter = story.encounters.find(
                (e) => e.id === this.encounterId
            );
            if (!encounter) return;

            if (!confirm(`Are you sure you want to remove ${monsterName}?`))
                return;

            encounter.monsters = encounter.monsters.filter(
                (m) => m.name !== monsterName
            );
            this.store.updateEncounter(this.storyId, encounter);

            // Update view
            if (this.monsterToView && this.monsterToView.name === monsterName) {
                if (encounter.monsters.length === 0) {
                    this.monsterToView = null;
                    this.monsterIndex = null;
                } else {
                    this.monsterIndex = Math.min(
                        this.monsterIndex,
                        encounter.monsters.length - 1
                    );
                    this.monsterToView = encounter.monsters[this.monsterIndex];
                }
            }
        },

        /** 🔁 Move monster up/down within encounter */
        moveMonster(index, direction) {
            const story = this.store.getStoryById(this.storyId);
            if (!story) return;

            const encounter = story.encounters.find(
                (e) => e.id === this.encounterId
            );
            if (!encounter) return;

            const newIndex = index + direction;
            if (newIndex < 0 || newIndex >= encounter.monsters.length) return;

            const [moved] = encounter.monsters.splice(index, 1);
            encounter.monsters.splice(newIndex, 0, moved);

            this.store.updateEncounter(this.storyId, encounter);

            // Keep current selection aligned
            if (this.monsterIndex === index) {
                this.monsterIndex = newIndex;
                this.monsterToView = encounter.monsters[newIndex];
            }
        },
    },
};
</script>

<style scoped>
#monsters {
    /* background-color: rgba(255, 255, 255, 0.05); */
    height: 100%; /* fill the viewport */
    display: grid;
    grid-template-columns: 4fr 1fr;
    grid-template-areas: "main sidebar";
    color: white;
    grid-gap: 0;
    padding: 0;
    border-radius: 0 0 8px 8px;
    border-bottom: 1px solid #444;
    border-left: 1px solid #444;
    border-right: 1px solid #444;
    overflow: hidden; /* prevent double scroll */
}

#main {
    grid-area: main;
    overflow-y: auto;
    height: 100%;
    padding: 1rem;

    /* hide scrollbar for all browsers */
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* IE 10+ */
}
#main::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera */
}

#sidebar {
    grid-area: sidebar;
    overflow-y: auto;
    height: 100%;
    padding: 1rem 1rem 5rem 1rem;
    background-color: rgba(70, 70, 70, 0.6);

    /* hide scrollbar */
    scrollbar-width: none;
    -ms-overflow-style: none;
}
#sidebar::-webkit-scrollbar {
    display: none;
}

.card {
    border: 1px solid rgba(255, 255, 255, 0.1);
    transition: all 0.2s ease;
}

.card:hover {
    border-color: #00bfff;
    background-color: rgba(0, 191, 255, 0.05);
}

.active-monster {
    border: 2px solid #00bfff !important;
    background-color: rgba(0, 191, 255, 0.15) !important;
}
</style>

